import { useEffect } from "react";
import { useAuth } from "@/lib/auth/context";
import { applyAccent } from "@/lib/utils/accent";

const STORAGE_ACCENT = "kd_accent";

// Brand default = Moss (#3A7245). Spójne z --kd-accent w index.css.
const DEFAULT_ACCENT = "#3A7245";

// Stary default zanim zmieniliśmy paletę. Migrujemy go silentnie na nowy
// — userzy którzy świadomie wybrali inny kolor (Niebieski/Fiolet/itd.)
// nie są dotknięci.
const LEGACY_DEFAULT_ACCENT = "#00875a";
const migrate = (c: string): string =>
  c.toLowerCase() === LEGACY_DEFAULT_ACCENT ? DEFAULT_ACCENT : c;

/**
 * Aplikuje preferencje (kolor akcentu) do DOM-a.
 *
 * Źródło prawdy:
 *   1. users/{uid}.prefs (po zalogowaniu) — synchronizowane między urządzeniami
 *   2. localStorage (przed zalogowaniem albo gdy prefs jeszcze nie ustawione)
 *   3. domyślne (#3A7245 — moss)
 *
 * localStorage trzymamy jako fast-path — bez czekania na Firestore na cold-start
 * shell ma już prawidłowy akcent, bez flashu.
 */
export function useApplyPrefs() {
  const { userDoc } = useAuth();

  // Cold-boot fast-path: zastosuj z localStorage zanim userDoc się załaduje.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const stored = localStorage.getItem(STORAGE_ACCENT) || DEFAULT_ACCENT;
    const accent = migrate(stored);
    applyAccent(accent);
    if (accent !== stored) localStorage.setItem(STORAGE_ACCENT, accent);
  }, []);

  // Po zalogowaniu — nadpisz z user doc i zsynchronizuj localStorage.
  useEffect(() => {
    const prefs = userDoc?.prefs;
    if (!prefs?.accent) return;
    const accent = migrate(prefs.accent);
    applyAccent(accent);
    localStorage.setItem(STORAGE_ACCENT, accent);
  }, [userDoc?.prefs]);
}
